import { t } from '@app/i18n';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { Icon, useColorMode, type ColorModePreference, type IconName } from '@zarya/theme';
import {
  Alert,
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  InfoTip,
  LoadingButton,
  SearchCombobox,
  TextArea,
  type ComboOption,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { API_BASE_URL } from '@app/api/client';
import { useIdentities } from '@app/main/Decisions';
import { useLabPeople } from '@app/main/Conversar';
import { ChatMessage } from './ChatMessage';
import { WhoAsks, type AskerTab } from './WhoAsks';
import { useChat } from './useChat';
import { isEdgeCut } from './Chat.api';
import { useBodyScrollLock } from './useBodyScrollLock';
import { MS_PER_CHAR } from './useTypewriter';
import { useChatDefaultAgent, useChatEntry } from './ChatEntry.api';
import { ChatEntryDrawer } from './ChatEntryDrawer';
import { initialAgent, protocolLabel, readLastAgent, shortAddress, writeLastAgent } from './ChatEntryModel';
import {
  EMPTY_CLAIMS,
  askerProfile,
  askerReady,
  claimsFromForm,
  tokenClaims,
  type Asker,
  type ClaimsForm,
} from './ChatModel';
import s from './Chat.module.css';

/**
 * Chat «Ask» (`/chat`, 04/09) — a tela que o cliente vê na demo de 09/09.
 *
 * A pessoa pergunta em linguagem natural. O generalista (`atendimento`) escolhe
 * a quem delegar e que tool pedir; o especialista escolhe a tool e a chama no
 * gateway; STS, gateway e PDP ficam no caminho, inteiros. O que a tela mostra é
 * isso: a resposta, digitada letra por letra, e sob ela a trilha compacta —
 * Pessoa → Agente → «escolheu: …» → Decisão — mais o que o modelo pediu e o
 * contrato não deixou. A fronteira de autoridade não se move; é o que se vê.
 *
 * ## Onde ela mora
 *
 * Fora do `Shell` (sem rail, sem topbar), como o Console de demonstração — mas
 * **com os tokens do produto**, no tema claro e no escuro: ela não é bastidor, é
 * o produto conversando. Papéis admin e approver, os das rotas `agentes`.
 *
 * ## Só REST
 *
 * A resposta chega inteira (`POST /api/v1/agents/{slug}/ask`) e a tela a
 * digita (~12 ms por letra; o clique conclui). Sem WebSocket nesta rodada (§4).
 *
 * ## A página não rola
 *
 * Como num chat: a moldura ocupa a janela inteira, o `body` fica com
 * `overflow: hidden` enquanto a rota está montada (`useBodyScrollLock`) e a
 * rolagem é da transcrição — e do painel lateral, se as claims crescerem
 * além da altura. Ao sair da rota o `body` volta ao que era.
 */

// `value` é a preferência gravada (código); o rótulo sai do catálogo no desenho.
const THEMES: ReadonlyArray<{ value: ColorModePreference; labelKey: string; icon: IconName }> = [
  { value: 'light', labelKey: 'chat.theme.light', icon: 'sun' },
  { value: 'dark', labelKey: 'chat.theme.dark', icon: 'moon' },
  { value: 'system', labelKey: 'chat.theme.system', icon: 'switch' },
];

/** O host com que a página fala — a chamada executa **de verdade** para onde ele aponta. */
function environmentHost(): string {
  const base = API_BASE_URL.trim();
  if (!base) return window.location.host;
  try {
    return new URL(base, window.location.href).host;
  } catch {
    return base;
  }
}

export interface ChatPageProps {
  /** Milissegundos por letra da resposta; os testes passam `0`. */
  msPerChar?: number;
}

export function ChatPage({ msPerChar = MS_PER_CHAR }: ChatPageProps) {
  useBodyScrollLock();

  const [tab, setTab] = useState<AskerTab>('person');
  const [person, setPerson] = useState<string | null>(null);
  const [claims, setClaims] = useState<ClaimsForm>(EMPTY_CLAIMS);
  const [agent, setAgent] = useState<string | null>(null);
  const [question, setQuestion] = useState('');

  const people = useLabPeople();
  // `com_contrato=true`, como a ConversePage: sem contrato ativo não há o que o
  // contrato conceder, e a pergunta sairia só para o modelo recusar tudo.
  const identities = useIdentities(true);
  const chat = useChat();
  const { preference, setPreference } = useColorMode();

  const agentOptions: ComboOption[] = useMemo(
    () =>
      (identities.data ?? [])
        .filter((identity) => identity.slug)
        .map((identity) => ({
          value: identity.slug as string,
          label: identity.slug as string,
          hint: identity.role ?? undefined,
          mono: true,
        })),
    [identities.data],
  );

  // The agent the Chat opens on (D8, aim-5wc): the one marked in the drawer; without a mark, the last one the person
  // used in this browser; without both, the first of the list. No agent name in the code: in the `aks-aim` the entry
  // agent is `customer-service`, and the old fixed `atendimento` opened the Chat on whatever came first.
  //
  // It waits for the mark (`isPending`), not for success: a mark that cannot be read falls to the next rule, and an
  // empty field would be worse in a demonstration.
  const defaultAgent = useChatDefaultAgent();
  useEffect(() => {
    if (agent || agentOptions.length === 0 || defaultAgent.isPending) return;
    setAgent(
      initialAgent(
        agentOptions.map((option) => option.value),
        defaultAgent.data?.agent ?? null,
        readLastAgent(),
      ),
    );
  }, [agent, agentOptions, defaultAgent.isPending, defaultAgent.data]);

  const chooseAgent = (value: string | null) => {
    setAgent(value);
    if (value) writeLastAgent(value);
  };

  const identityId = useMemo(
    () => (identities.data ?? []).find((identity) => identity.slug === agent)?.id ?? null,
    [identities.data, agent],
  );
  const entry = useChatEntry(identityId);
  const canEditEntry = useCanOperar('updateChatEntry');
  const [entryOpen, setEntryOpen] = useState(false);

  const asker: Asker | null = useMemo(() => {
    if (tab === 'person') {
      const found = (people.data ?? []).find((p) => p.sub === person);
      return found ? { kind: 'person', person: found } : null;
    }
    return { kind: 'claims', claims: claimsFromForm(claims) };
  }, [tab, person, people.data, claims]);

  const readyToAsk = askerReady(asker) && Boolean(agent) && question.trim() !== '' && !chat.sending;

  const transcript = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = transcript.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [chat.messages]);

  function send() {
    if (!readyToAsk || !asker || !agent) return;
    chat.send({ agent, asker, question: question.trim() });
    writeLastAgent(agent);
    setQuestion('');
  }

  function onComposerKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter envia; Shift+Enter quebra a linha — o costume de todo chat.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  }

  const withoutIdentities = identities.isSuccess && agentOptions.length === 0;

  return (
    <div className={s.page} data-chat="">
      <header className={s.header} aria-label={t('chat.page.regionLabel')}>
        <div className={s.brand}>
          <span className={s.brandMark} aria-hidden="true">
            <Icon name="shield" size="sm" />
          </span>
          <div>
            <p className={s.brandName}>360 Agentic Identity</p>
            <h1 className={s.title}>{t('chat.page.title')}</h1>
          </div>
        </div>
        <p className={s.tagline}>
          {t('chat.page.tagline')}
        </p>
        <div className={s.headerRight}>
          <div className={s.theme} role="group" aria-label={t('chat.theme.label')}>
            {THEMES.map((theme) => (
              <button
                key={theme.value}
                type="button"
                className={`${s.themeButton} ${preference === theme.value ? s.themeOn : ''}`}
                aria-pressed={preference === theme.value}
                title={t(theme.labelKey)}
                onClick={() => setPreference(theme.value)}
              >
                <Icon name={theme.icon} size="sm" aria-hidden="true" />
                <span className="sr-only">{t(theme.labelKey)}</span>
              </button>
            ))}
          </div>
          <p className={s.environment}>
            <span className={s.environmentLabel}>{t('chat.page.environment')}</span>
            <span className={s.mono} data-testid="chat-environment">
              {environmentHost()}
            </span>
          </p>
          <Link className={s.back} to="/">
            <Icon name="chevron-left" size="sm" aria-hidden="true" />
            {t('chat.page.backToConsole')}
          </Link>
        </div>
      </header>

      <main className={s.body}>
        <aside className={s.side} aria-label={t('chat.page.askConfig')}>
          {/* Subtítulo curto para caber ao lado do título a 1366×768 (o longo
              quebrava linha e custava 24px do orçamento do painel); a frase
              inteira fica no ⓘ. */}
          <Card
            title={t('chat.who.title')}
            subtitle={t('chat.who.subtitle')}
            tip={t('chat.who.tip')}
            fill
          >
            {people.isError ? (
              <ErrorBanner
                error={people.error}
                title={t('chat.who.peopleError')}
                onRetry={() => void people.refetch()}
              />
            ) : null}
            <WhoAsks
              tab={tab}
              onTab={setTab}
              people={people.data ?? []}
              peopleLoading={people.isLoading}
              peopleError={people.isError ? people.error : null}
              person={person}
              onPerson={setPerson}
              claims={claims}
              onClaims={setClaims}
            />
          </Card>

          <Card title={t('chat.agent.title')} subtitle={t('chat.agent.subtitle')} fill>
            {identities.isError ? (
              <ErrorBanner
                error={identities.error}
                title={t('chat.agent.listError')}
                onRetry={() => void identities.refetch()}
              />
            ) : null}
            {withoutIdentities ? (
              <Alert tone="info" title={t('chat.agent.emptyTitle')}>
                {t('chat.agent.emptyBody')}
              </Alert>
            ) : null}
            <Field
              label={t('chat.agent.label')}
              required
              tip={t('chat.agent.tip')}
            >
              {({ id, className: _class, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('chat.agent.label')}
                  options={agentOptions}
                  value={agent}
                  placeholder={identities.isLoading ? t('chat.agent.loading') : t('chat.agent.placeholder')}
                  emptyMessage={t('chat.agent.empty')}
                  onChange={chooseAgent}
                />
              )}
            </Field>
            {identityId ? (
              <div className={s.entryLine} data-testid="chat-entry-line" data-custom={entry.data?.custom ? 'true' : 'false'}>
                <span className={s.entryLineLabel}>
                  {t('chat.entry.label')}
                  <InfoTip text={t('chat.entry.tip')} size="xs" />
                </span>
                {entry.data ? (
                  <span className={s.mono} data-testid="chat-entry-summary">
                    {t(entry.data.custom ? 'chat.entry.summary.custom' : 'chat.entry.summary.default', {
                      address: shortAddress(entry.data.entry),
                      protocol: protocolLabel(entry.data.entry.protocol),
                    })}
                  </span>
                ) : null}
                <Button variant="soft" size="sm" onClick={() => setEntryOpen(true)} data-testid="chat-entry-open">
                  {t(canEditEntry ? 'chat.entry.configure' : 'chat.entry.view')}
                </Button>
              </div>
            ) : null}
          </Card>

          <Card
            title={t('chat.token.title')}
            subtitle={tab === 'person' ? t('chat.token.preSigned') : t('chat.token.generated')}
            tip={t('chat.token.tip')}
            fill
          >
            <TokenCard asker={asker} />
          </Card>
        </aside>

        <section className={s.conversation} aria-label="Conversa">
          <Card title={t('chat.conversation.title')} fill className={s.conversationCard}>
            {chat.error && isEdgeCut(chat.error) ? (
              // aim-988: a espera passou de um minuto. A pergunta pode ter chegado ao agente, então o título não
              // diz «não foi enviada»; o corpo diz o motivo, e não o erro genérico da página HTML da borda.
              <Alert tone="danger" title={t('chat.conversation.edgeCut.title')}>
                <p data-testid="chat-edge-cut">{t('chat.conversation.edgeCut.body')}</p>
                <Button variant="soft" size="sm" onClick={chat.clearError}>
                  {t('chat.conversation.clear')}
                </Button>
              </Alert>
            ) : chat.error ? (
              <ErrorBanner
                error={chat.error}
                title={t('chat.conversation.sendError')}
                onRetry={chat.clearError}
                retryLabel={t('chat.conversation.clear')}
              />
            ) : null}

            <div className={s.transcript} ref={transcript} data-testid="chat-transcript">
              {chat.messages.length === 0 ? (
                <div className={s.empty}>
                  <span className={s.emptyMark} aria-hidden="true">
                    <Icon name="robot" size="lg" />
                  </span>
                  <p className={s.emptyTitle}>{t('chat.conversation.empty')}</p>
                  <p className={s.emptyText}>
                    {t('chat.conversation.emptyHint')}
                  </p>
                </div>
              ) : null}
              {chat.messages.map((message) => (
                <ChatMessage key={message.id} message={message} msPerChar={msPerChar} />
              ))}
            </div>

            <div className={s.composer}>
              <Field label={t('chat.conversation.questionLabel')} required className={s.composerField}>
                {({ id, className, ...aria }) => (
                  <TextArea
                    id={id}
                    className={className}
                    {...aria}
                    rows={2}
                    value={question}
                    placeholder={t('chat.conversation.questionPlaceholder')}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={onComposerKey}
                    data-testid="chat-composer"
                  />
                )}
              </Field>
              <div className={s.composerActions}>
                <p className={s.composerHint}>
                  {t('chat.conversation.questionHelp')}
                </p>
                <LoadingButton
                  loading={chat.sending}
                  disabled={!readyToAsk}
                  onClick={send}
                  icon="arrow-right"
                  data-testid="chat-send"
                >
                  {t('chat.conversation.ask')}
                </LoadingButton>
              </div>
            </div>
          </Card>
        </section>
      </main>

      {identityId && agent ? (
        <ChatEntryDrawer
          open={entryOpen}
          identityId={identityId}
          agent={agent}
          canEdit={canEditEntry}
          onClose={() => setEntryOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** O cartão «token da pessoa»: as claims, sem o JWT. */
function TokenCard({ asker }: { asker: Asker | null }) {
  if (!asker || !askerReady(asker)) {
    return <p className={s.tokenEmpty}>{t('chat.token.chooseFirst')}</p>;
  }
  const profile = askerProfile(asker);
  return (
    <div className={s.token} data-testid="chat-token-card">
      <div className={s.tokenHead}>
        <Badge tone={asker.kind === 'person' ? 'primary' : 'neutral'}>
          {asker.kind === 'person' ? t('chat.token.fromPerson') : t('chat.token.fromClaims')}
        </Badge>
        {profile ? <Badge tone="neutral">{profile}</Badge> : null}
      </div>
      <dl className={s.tokenClaims}>
        {tokenClaims(asker).map((claim) => (
          <div key={claim.key} className={s.tokenClaim}>
            <dt className={s.mono}>{claim.key}</dt>
            <dd className={s.mono}>{claim.value || <span className={s.tokenEmptyValue}>{t('chat.token.emptyValue')}</span>}</dd>
          </div>
        ))}
      </dl>
      <p className={s.tokenNote}>{t('chat.token.neverShown')}</p>
    </div>
  );
}
